import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HiCurrencyRupee, HiEye, HiPencil, HiCash, HiX, HiPlus, HiSearch, HiDownload } from 'react-icons/hi';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { useNotification } from '../context/NotificationContext';
import Tooltip from '../components/Tooltip';
import ConfirmationModal from '../components/ConfirmationModal';
import CitySelect from '../components/CitySelect';
import { getErrorMessage } from '../utils/errorHandler';
import { formatPKR } from '../utils/currencyFormatter';
import { API } from '../config';
import axios from 'axios';
import { downloadMonthlySalarySlipPdf, downloadAnnualSalarySlipPdf } from '../utils/salarySlipPdf';
import './Salary.css';

const Salary = () => {
  const { user } = useAuth();
  const { hasBranches, effectiveBranchId, isAdmin, currentBranch } = useBranch();
  const { success, error } = useNotification();
  const [salaryOverview, setSalaryOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState(user?.business_id || user?.businessId || '');
  const [month, setMonth] = useState(() => String(new Date().getMonth() + 1));
  const [year, setYear] = useState(() => String(new Date().getFullYear()));
  const [fundAccounts, setFundAccounts] = useState([]);
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [salaryFormData, setSalaryFormData] = useState({
    amount: '',
    periodMonth: '',
    periodYear: '',
    description: '',
    paymentMethod: 'cash',
    accountId: '',
    transactionDate: new Date().toISOString().split('T')[0]
  });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: '', phone: '', city: '', salary: '', notes: '', employeeAdvance: '', salaryPaidForCurrentMonth: false });
  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [selectedEmployeeForAdvance, setSelectedEmployeeForAdvance] = useState(null);
  const [advanceFormData, setAdvanceFormData] = useState({
    amount: '',
    periodMonth: '',
    periodYear: '',
    accountId: '',
    description: '',
    paymentMethod: 'cash',
    transactionDate: new Date().toISOString().split('T')[0]
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerEmployee, setLedgerEmployee] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerFilterType, setLedgerFilterType] = useState('all');
  const [ledgerFilterMonth, setLedgerFilterMonth] = useState('');
  const [ledgerFilterYear, setLedgerFilterYear] = useState('');
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [addEmployeeFormData, setAddEmployeeFormData] = useState({
    name: '',
    phone: '',
    city: '',
    salary: '',
    notes: '',
    employeeAdvance: '',
    salaryPaidForCurrentMonth: false,
    deductCurrentMonthSalaryFromAccountId: ''
  });
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [salarySlipDownloadingId, setSalarySlipDownloadingId] = useState(null);
  const [salarySlipMenu, setSalarySlipMenu] = useState(null);

  const businessId = user?.role === 'super_admin' ? selectedBusinessId : (user?.business_id || user?.businessId);

  // "Salary already given" checkbox = this calendar month (matches Parties employee form). Advance = from current month's salary.
  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();
  const currentPeriodStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const currentMonthName = monthNames[currentMonth - 1];

  const formatBalance = (balance) => {
    const num = parseFloat(balance) || 0;
    if (num > 0) return <span className="balance-positive">{formatPKR(num)} (To receive)</span>;
    if (num < 0) return <span className="balance-negative">{formatPKR(Math.abs(num))} (We owe)</span>;
    return <span className="balance-zero">{formatPKR(0)}</span>;
  };

  useEffect(() => {
    if (user?.role === 'super_admin') {
      axios.get(`${API}/businesses/all`)
        .then(res => {
          const list = res.data.businesses || [];
          setBusinesses(list);
          if (!selectedBusinessId && list.length > 0) setSelectedBusinessId(list[0].id);
        })
        .catch(() => setBusinesses([]));
    }
  }, [user?.role]);

  useEffect(() => {
    if (!businessId) {
      setBusinessName('');
      return;
    }
    axios
      .get(`${API}/businesses/${businessId}`)
      .then((res) => {
        const b = res.data?.business ?? res.data;
        setBusinessName(String(b?.name || '').trim());
      })
      .catch(() => setBusinessName(''));
  }, [businessId]);

  useEffect(() => {
    if (businessId) {
      axios.get(`${API}/funds/business/${businessId}/accounts`)
        .then(res => setFundAccounts(res.data.accounts || []))
        .catch(() => setFundAccounts([]));
    }
  }, [businessId]);

  useEffect(() => {
    if (!hasBranches && showAddEmployeeModal) {
      setShowAddEmployeeModal(false);
      setAddEmployeeFormData({ name: '', phone: '', city: '', salary: '', notes: '', employeeAdvance: '', salaryPaidForCurrentMonth: false, deductCurrentMonthSalaryFromAccountId: '' });
    }
  }, [hasBranches]);

  useEffect(() => {
    if (!salarySlipMenu) return undefined;
    const closeMenu = () => setSalarySlipMenu(null);
    const closeOnViewportChange = () => setSalarySlipMenu(null);
    document.addEventListener('click', closeMenu);
    window.addEventListener('scroll', closeOnViewportChange, true);
    window.addEventListener('resize', closeOnViewportChange);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('scroll', closeOnViewportChange, true);
      window.removeEventListener('resize', closeOnViewportChange);
    };
  }, [salarySlipMenu]);

  const salaryOverviewUrl = () => {
    const base = `${API}/parties/business/${businessId}/salary-overview?month=${month}&year=${year}`;
    return effectiveBranchId ? `${base}&branchId=${effectiveBranchId}` : base;
  };

  useEffect(() => {
    if (!businessId || !month || !year) {
      setLoading(false);
      setSalaryOverview(null);
      return;
    }
    setLoading(true);
    axios.get(salaryOverviewUrl())
      .then(res => {
        setSalaryOverview(res.data);
      })
      .catch(() => setSalaryOverview(null))
      .finally(() => setLoading(false));
  }, [businessId, month, year, effectiveBranchId]);

  const fetchLedger = async (partyId, filterType = 'all', filterMonth = '', filterYear = '') => {
    try {
      const useStatement = filterType !== 'all' || filterMonth || filterYear;
      const currentYear = new Date().getFullYear();
      const yr = filterYear ? parseInt(filterYear, 10) : currentYear;
      const from = `${yr}-01-01`;
      const to = yr === currentYear ? new Date().toISOString().split('T')[0] : `${yr}-12-31`;
      if (useStatement) {
        const params = new URLSearchParams({ from, to });
        if (filterType !== 'all') params.set('type', filterType);
        if (filterMonth) params.set('month', filterMonth);
        if (filterYear) params.set('year', filterYear);
        const response = await axios.get(`${API}/parties/${partyId}/statement?${params.toString()}`);
        const list = response.data.ledger || [];
        setLedgerEntries(list.map(e => ({ ...e, balance_after: e.running_balance ?? e.balance_after })));
      } else {
        const response = await axios.get(`${API}/parties/${partyId}/ledger`);
        setLedgerEntries(response.data.ledger || []);
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleViewLedger = async (emp) => {
    setLedgerEmployee(emp);
    setLedgerFilterType('all');
    setLedgerFilterMonth('');
    setLedgerFilterYear('');
    await fetchLedger(emp.id);
    setShowLedgerModal(true);
  };

  const handleToggleSalarySlipMenu = (emp, event) => {
    event.stopPropagation();
    if (salarySlipMenu?.employee?.id === emp.id) {
      setSalarySlipMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 92;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight + 12;
    const top = openAbove ? rect.top - menuHeight - 6 : rect.bottom + 6;
    setSalarySlipMenu({
      employee: emp,
      top: Math.max(8, top),
      left: rect.right,
      openAbove,
    });
  };

  const handleDownloadMonthlySalarySlip = async (emp) => {
    if (!salaryOverview) {
      error('Load salary overview first.');
      return;
    }
    setSalarySlipMenu(null);
    setSalarySlipDownloadingId(emp.id);
    try {
      const periodMonth = parseInt(month, 10) || new Date().getMonth() + 1;
      const periodYear = parseInt(year, 10) || new Date().getFullYear();
      const from = `${periodYear}-01-01`;
      const to = `${periodYear}-12-31`;
      const params = new URLSearchParams({ from, to, month: String(periodMonth), year: String(periodYear) });
      const statementRes = await axios.get(`${API}/parties/${emp.id}/statement?${params.toString()}`);
      const monthData = statementRes.data || {};
      await downloadMonthlySalarySlipPdf({
        businessName: businessName || 'Safari Club',
        branchName: currentBranch?.name || '',
        employee: emp,
        periodLabel: salaryOverview.period_label,
        periodMonth,
        periodYear,
        generatedBy: user?.name || user?.fullName || user?.username || '',
        ledgerEntries: monthData.ledger || []
      });
      success(`Monthly salary slip for ${salaryOverview.period_label} downloaded.`);
    } catch (err) {
      console.error(err);
      error(getErrorMessage(err) || 'Could not generate monthly salary slip PDF.');
    } finally {
      setSalarySlipDownloadingId(null);
    }
  };

  const handleDownloadAnnualSalarySlip = async (emp) => {
    if (!salaryOverview) {
      error('Load salary overview first.');
      return;
    }
    setSalarySlipMenu(null);
    setSalarySlipDownloadingId(emp.id);
    try {
      const periodYear = parseInt(year, 10) || new Date().getFullYear();
      const from = `${periodYear}-01-01`;
      const to = `${periodYear}-12-31`;
      const statementRes = await axios.get(`${API}/parties/${emp.id}/statement?from=${from}&to=${to}`);
      const yearlyData = statementRes.data || {};
      await downloadAnnualSalarySlipPdf({
        businessName: businessName || 'Safari Club',
        branchName: currentBranch?.name || '',
        employee: emp,
        periodLabel: salaryOverview.period_label,
        year: periodYear,
        generatedBy: user?.name || user?.fullName || user?.username || '',
        ledgerEntries: yearlyData.ledger || [],
        monthlySummary: yearlyData.summary_by_month || []
      });
      success(`Annual salary slip for ${periodYear} downloaded.`);
    } catch (err) {
      console.error(err);
      error(getErrorMessage(err) || 'Could not generate annual salary slip PDF.');
    } finally {
      setSalarySlipDownloadingId(null);
    }
  };

  // Backward-compatible alias (older button handlers / hot-reload cache)
  const handleDownloadSalarySlip = handleDownloadMonthlySalarySlip;

  const handleLedgerFilterChange = async () => {
    if (!ledgerEmployee) return;
    await fetchLedger(ledgerEmployee.id, ledgerFilterType, ledgerFilterMonth, ledgerFilterYear);
  };

  const handlePaySalary = async (emp) => {
    const periodMonth = month || String(new Date().getMonth() + 1);
    const periodYear = year || String(new Date().getFullYear());
    try {
      const res = await axios.get(`${API}/parties/${emp.id}/salary-outstanding?month=${periodMonth}&year=${periodYear}`);
      const data = res.data || {};
      const outstanding = parseFloat(data.outstanding) ?? parseFloat(emp.outstanding_for_month) ?? 0;
      if (outstanding <= 0) {
        success('Already settled. No payment required for this month.');
        return;
      }
      const now = new Date();
      const monthlySalary = parseFloat(emp.monthly_salary) || 0;
      const advanceBalance = parseFloat(emp.balance) || 0;
      const netToPay = monthlySalary > 0 ? Math.max(0, monthlySalary - advanceBalance) : 0;
      const defaultPayAmount =
        monthlySalary > 0 && netToPay > 0 ? Math.min(outstanding, netToPay) : outstanding;
      const employeeWithOutstanding = { ...emp, outstanding_for_month: outstanding, paid_for_month: data.paid_for_month, advances_allocated: data.advances_allocated };
      setSelectedEmployee(employeeWithOutstanding);
      setSalaryFormData({
        amount: String(Number(defaultPayAmount.toFixed(2))),
        periodMonth,
        periodYear,
        description: '',
        paymentMethod: 'cash',
        accountId: '',
        transactionDate: now.toISOString().split('T')[0]
      });
      setShowSalaryModal(true);
      if (businessId) {
        axios.get(`${API}/funds/business/${businessId}/accounts`)
          .then(res => setFundAccounts(res.data.accounts || []))
          .catch(() => {});
      }
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleSalarySubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployee) return;
    const amount = parseFloat(salaryFormData.amount);
    const outstanding = parseFloat(selectedEmployee.outstanding_for_month) ?? 0;
    if (outstanding <= 0) {
      error('This month is already settled. No payment required.');
      return;
    }
    if (amount <= 0 || amount > outstanding) {
      error(`Amount must be between 0 and ${outstanding.toFixed(2)} (outstanding for this month).`);
      return;
    }
    if (!salaryFormData.accountId) {
      error('Please select the account or cash to pay from.');
      return;
    }
    const selectedAccount = fundAccounts.find(a => String(a.id) === String(salaryFormData.accountId));
    const availableBalance = parseFloat(selectedAccount?.current_balance ?? selectedAccount?.currentBalance ?? 0) || 0;
    if (amount > availableBalance) {
      error(`Insufficient balance. Available in ${selectedAccount?.name || 'selected account'}: ${formatPKR(availableBalance)}.`);
      return;
    }
    try {
      const payload = {
        amount,
        paymentMethod: salaryFormData.paymentMethod,
        transactionDate: salaryFormData.transactionDate,
        periodMonth: parseInt(salaryFormData.periodMonth),
        periodYear: parseInt(salaryFormData.periodYear),
        accountId: parseInt(salaryFormData.accountId)
      };
      if (salaryFormData.description) payload.description = salaryFormData.description;
      await axios.post(`${API}/parties/${selectedEmployee.id}/salary`, payload);
      success('Salary payment recorded!');
      setShowSalaryModal(false);
      if (showLedgerModal && ledgerEmployee?.id === selectedEmployee.id) {
        await fetchLedger(selectedEmployee.id);
        const res = await axios.get(salaryOverviewUrl());
        setSalaryOverview(res.data);
        const updated = res.data?.employees?.find(e => e.id === ledgerEmployee.id);
        if (updated) setLedgerEmployee(updated);
      } else {
        refreshSalaryOverview();
      }
      setSelectedEmployee(null);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const refreshSalaryOverview = () => {
    if (!businessId || !month || !year) return;
    axios.get(salaryOverviewUrl())
      .then(res => setSalaryOverview(res.data))
      .catch(() => setSalaryOverview(null));
  };

  const handleEdit = async (emp) => {
    try {
      const res = await axios.get(`${API}/parties/${emp.id}`);
      const p = res.data.party || res.data;
      setEditingParty(p);
      const meta = p.meta || {};
      const paidPeriod = meta.salaryPaidForPeriod || meta.salary_paid_for_period || '';
      setEditFormData({
        name: p.name || '',
        phone: p.phone || '',
        city: p.city || '',
        salary: p.salary != null ? String(p.salary) : '',
        notes: p.notes || '',
        employeeAdvance: meta.employeeAdvance != null ? String(meta.employeeAdvance) : (meta.employee_advance != null ? String(meta.employee_advance) : ''),
        salaryPaidForCurrentMonth: Boolean(paidPeriod === currentPeriodStr)
      });
      setShowEditModal(true);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingParty) return;
    try {
      await axios.put(`${API}/parties/${editingParty.id}`, {
        name: editFormData.name,
        phone: editFormData.phone || null,
        city: editFormData.city || null,
        notes: editFormData.notes || null,
        salary: editFormData.salary ? parseFloat(editFormData.salary) : null,
        isEmployee: true,
        employeeAdvance: editFormData.employeeAdvance ? parseFloat(editFormData.employeeAdvance) : 0,
        salaryPaidForCurrentMonth: Boolean(editFormData.salaryPaidForCurrentMonth),
        ...(editFormData.salaryPaidForCurrentMonth ? { salaryPaidForPeriod: currentPeriodStr } : {})
      });
      success('Employee updated!');
      setShowEditModal(false);
      setEditingParty(null);
      refreshSalaryOverview();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleRecordAdvance = (emp) => {
    // Advance is from current month's salary (e.g. in March, advance = from March salary)
    setSelectedEmployeeForAdvance(emp);
    setAdvanceFormData({
      amount: '',
      periodMonth: String(currentMonth),
      periodYear: String(currentYear),
      accountId: '',
      description: '',
      paymentMethod: 'cash',
      transactionDate: new Date().toISOString().split('T')[0]
    });
    setShowAdvanceModal(true);
  };

  const handleAdvanceSubmit = async (e) => {
    e.preventDefault();
    if (!selectedEmployeeForAdvance) return;
    if (!advanceFormData.periodMonth || !advanceFormData.periodYear) {
      error('Please select the month and year this advance is allocated to (it will reduce that month\'s outstanding).');
      return;
    }
    try {
      const payload = {
        amount: parseFloat(advanceFormData.amount),
        periodMonth: parseInt(advanceFormData.periodMonth, 10),
        periodYear: parseInt(advanceFormData.periodYear, 10),
        paymentMethod: advanceFormData.paymentMethod,
        transactionDate: advanceFormData.transactionDate
      };
      if (advanceFormData.accountId) payload.accountId = parseInt(advanceFormData.accountId);
      if (advanceFormData.description) payload.description = advanceFormData.description;
      await axios.post(`${API}/parties/${selectedEmployeeForAdvance.id}/advance`, payload);
      success('Advance recorded!');
      setShowAdvanceModal(false);
      if (showLedgerModal && ledgerEmployee?.id === selectedEmployeeForAdvance.id) {
        await fetchLedger(selectedEmployeeForAdvance.id);
        const res = await axios.get(salaryOverviewUrl());
        setSalaryOverview(res.data);
        const updated = res.data?.employees?.find(e => e.id === ledgerEmployee.id);
        if (updated) setLedgerEmployee(updated);
      } else {
        refreshSalaryOverview();
      }
      setSelectedEmployeeForAdvance(null);
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleDelete = (emp) => {
    if (emp.balance !== 0) {
      error('Delete only when balance is zero. Clear or adjust balance first.');
      return;
    }
    setEmployeeToDelete(emp);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!employeeToDelete) return;
    try {
      await axios.delete(`${API}/parties/${employeeToDelete.id}`);
      success('Employee deleted.');
      setShowDeleteConfirm(false);
      setEmployeeToDelete(null);
      refreshSalaryOverview();
    } catch (err) {
      error(getErrorMessage(err));
      setShowDeleteConfirm(false);
      setEmployeeToDelete(null);
    }
  };

  const handleAddEmployeeSubmit = async (e) => {
    e.preventDefault();
    if (!hasBranches) {
      error('Create a branch first. Every employee is linked to a branch—go to Branches to add one.');
      return;
    }
    if (isAdmin && !effectiveBranchId) {
      error('Select a branch from the header dropdown. The employee will be linked to that branch.');
      return;
    }
    if (!businessId) {
      error('Please select a business.');
      return;
    }
    try {
      const payload = {
        name: addEmployeeFormData.name.trim(),
        phone: addEmployeeFormData.phone.trim() || null,
        city: addEmployeeFormData.city.trim() || null,
        notes: addEmployeeFormData.notes.trim() || null,
        businessId: parseInt(businessId),
        openingBalance: 0,
        isEmployee: true,
        salary: addEmployeeFormData.salary ? parseFloat(addEmployeeFormData.salary) : null,
        employeeAdvance: addEmployeeFormData.employeeAdvance ? parseFloat(addEmployeeFormData.employeeAdvance) : 0,
        salaryPaidForCurrentMonth: Boolean(addEmployeeFormData.salaryPaidForCurrentMonth)
      };
      if (addEmployeeFormData.salaryPaidForCurrentMonth) payload.salaryPaidForPeriod = currentPeriodStr;
      if (effectiveBranchId) payload.branchId = effectiveBranchId;
      if (!addEmployeeFormData.salaryPaidForCurrentMonth && addEmployeeFormData.deductCurrentMonthSalaryFromAccountId) {
        payload.deductCurrentMonthSalaryFromAccountId = parseInt(addEmployeeFormData.deductCurrentMonthSalaryFromAccountId, 10);
      }
      await axios.post(`${API}/parties`, payload);
      success(currentBranch ? `Employee added and linked to ${currentBranch.name}.` : 'Employee added.');
      setShowAddEmployeeModal(false);
      setAddEmployeeFormData({ name: '', phone: '', city: '', salary: '', notes: '', employeeAdvance: '', salaryPaidForCurrentMonth: false, deductCurrentMonthSalaryFromAccountId: '' });
      refreshSalaryOverview();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const years = (() => {
    const y = new Date().getFullYear();
    const arr = [];
    for (let i = y + 1; i >= 2015; i--) arr.push(i);
    return arr;
  })();

  return (
    <Layout>
      <div className="salary-page">
        <div className="page-header">
          <div>
            <h1>Monthly salary</h1>
            <p className="page-subtitle">Salary is typically paid at the end of the month. Employees can receive advances from upcoming salary; such months show as partially paid until the remainder is paid. View and record salary by month; history is in each employee&apos;s ledger below.</p>
          </div>
          <Tooltip text={!hasBranches ? 'Create a branch first—every employee is linked to a branch' : 'Add employee (linked to current branch)'} position="bottom">
            <button
              type="button"
              className="btn-primary add-employee-btn"
              onClick={() => { if (hasBranches) setShowAddEmployeeModal(true); }}
              disabled={!hasBranches}
              aria-disabled={!hasBranches}
            >
              <HiPlus /> Add Employee
            </button>
          </Tooltip>
        </div>

        {!hasBranches && (
          <div className="banner no-branches-banner">
            <strong>You need a branch before you can add employees.</strong> Every employee is linked to a branch. Create your first branch, then come back to add employees. <a href="/branches">Go to Branches →</a>
          </div>
        )}
        {user?.role === 'super_admin' && businesses.length > 0 && (
          <div className="filters-container">
            <div className="form-group">
              <label>Business</label>
              <select value={selectedBusinessId} onChange={(e) => setSelectedBusinessId(e.target.value)}>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="salary-overview-section">
          <div className="salary-overview-controls">
            <div className="form-group">
              <label>Month</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)}>
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                  <option key={m} value={m}>{new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Year</label>
              <select value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
              </select>
            </div>
            <div className="form-group salary-employee-search-wrap">
              <label>Search employee</label>
              <div className="salary-employee-search-inner">
                <HiSearch className="salary-employee-search-icon" aria-hidden="true" />
                <input
                  type="text"
                  className="salary-employee-search-input"
                  placeholder="Search by name..."
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  aria-label="Search employees by name"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <Loader message="Loading salary overview..." variant="block" />
          ) : salaryOverview ? (
            <div className="salary-overview-table-wrap">
              <table className="data-table salary-overview-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Monthly salary (PKR)</th>
                    <th>Paid/Allocated for {salaryOverview.period_label}</th>
                    <th>Outstanding</th>
                    <th>Status</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(!salaryOverview.employees || salaryOverview.employees.length === 0) ? (
                    <tr><td colSpan={7} className="empty-cell">No employees. Add employees from Parties (Inventory).</td></tr>
                  ) : (
                    (() => {
                      const q = (employeeSearchTerm || '').toLowerCase().trim();
                      const filtered = !q
                        ? salaryOverview.employees
                        : salaryOverview.employees.filter((emp) => (emp.name || '').toLowerCase().includes(q));
                      if (filtered.length === 0) {
                        return <tr><td colSpan={7} className="empty-cell">No employees match &quot;{employeeSearchTerm}&quot;.</td></tr>;
                      }
                      return filtered.map((emp) => (
                      <tr key={emp.id}>
                        <td className="font-semibold">{emp.name}</td>
                        <td>{formatPKR(emp.monthly_salary)}</td>
                        <td>{formatPKR((emp.paid_for_month || 0) + (emp.advances_allocated_to_month || 0))}</td>
                        <td>{formatPKR(emp.outstanding_for_month ?? 0)}</td>
                        <td>
                          <Tooltip
                            text={emp.status === 'partial' && (emp.advances_allocated_to_month || 0) > 0
                              ? `Advance given for this month; pay remaining ${formatPKR(emp.outstanding_for_month ?? 0)} at month end.`
                              : emp.status === 'partial'
                                ? 'Some payment recorded; remaining outstanding.'
                                : emp.status === 'settled'
                                  ? 'Already settled for this month.'
                                  : 'Unpaid for this month.'}
                            position="top"
                          >
                            <span className={`salary-status-badge ${emp.status}`}>
                              {emp.status === 'settled' ? 'Already Settled' : emp.status === 'partial' ? 'Partially paid' : 'Unpaid'}
                            </span>
                          </Tooltip>
                        </td>
                        <td>
                          {emp.balance < 0 ? <span className="balance-negative">{formatPKR(Math.abs(emp.balance))} (We owe)</span> : emp.balance > 0 ? <span className="balance-positive">{formatPKR(emp.balance)} (To receive)</span> : formatPKR(0)}
                        </td>
                        <td>
                          <div className="salary-actions">
                            <Tooltip text="View ledger" position="top">
                              <button type="button" className="action-btn view-btn" onClick={() => handleViewLedger(emp)} title="View ledger">
                                <HiEye />
                              </button>
                            </Tooltip>
                            <div className="salary-slip-menu-wrap">
                              <Tooltip text="Download salary slip" position="top">
                                <button
                                  type="button"
                                  className="action-btn salary-slip-pdf-btn"
                                  onClick={(e) => handleToggleSalarySlipMenu(emp, e)}
                                  title="Download salary slip"
                                  disabled={salarySlipDownloadingId === emp.id}
                                  aria-busy={salarySlipDownloadingId === emp.id}
                                  aria-expanded={salarySlipMenu?.employee?.id === emp.id}
                                  aria-haspopup="menu"
                                >
                                  <HiDownload />
                                </button>
                              </Tooltip>
                            </div>
                            <Tooltip text="Edit employee" position="top">
                              <button type="button" className="action-btn edit-btn" onClick={() => handleEdit(emp)} title="Edit">
                                <HiPencil />
                              </button>
                            </Tooltip>
                            <Tooltip text={emp.status === 'settled' ? 'Already settled for this month' : 'Pay salary (only remaining outstanding)'} position="top">
                              <button
                                type="button"
                                className="action-btn salary-btn"
                                onClick={() => handlePaySalary(emp)}
                                title={emp.status === 'settled' ? 'Already settled' : 'Pay salary'}
                                disabled={emp.status === 'settled'}
                              >
                                <HiCurrencyRupee />
                              </button>
                            </Tooltip>
                            <Tooltip text="Record advance" position="top">
                              <button type="button" className="action-btn advance-btn" onClick={() => handleRecordAdvance(emp)} title="Record advance">
                                <HiCash />
                              </button>
                            </Tooltip>
                            <Tooltip text={emp.balance === 0 ? 'Delete employee' : 'Delete only when balance is zero'} position="top">
                              <button
                                type="button"
                                className="action-btn delete-btn"
                                onClick={() => handleDelete(emp)}
                                title="Delete"
                                disabled={emp.balance !== 0}
                              >
                                <HiX />
                              </button>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ));
                    })()
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">Unable to load salary overview. Select a business if you have multiple.</div>
          )}
        </div>

        {/* Pay Salary Modal */}
        {showSalaryModal && selectedEmployee && (
          <div className="modal-overlay" onClick={() => setShowSalaryModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Pay Salary – {selectedEmployee.name}</h2>
              {parseFloat(selectedEmployee.balance) > 0 && (
                <div className="salary-advance-balance-top">
                  <span className="salary-advance-label">Advance balance (to receive from salary):</span>
                  <span className="salary-advance-value balance-positive">{formatPKR(selectedEmployee.balance)}</span>
                </div>
              )}
              {(() => {
                const outstanding = parseFloat(selectedEmployee.outstanding_for_month) ?? 0;
                const isSettled = outstanding <= 0;
                const monthlySalary = parseFloat(selectedEmployee.monthly_salary) || 0;
                const advanceBalance = parseFloat(selectedEmployee.balance) || 0;
                const nextMonth = parseInt(month, 10) === 12 ? 1 : parseInt(month, 10) + 1;
                const nextYear = parseInt(month, 10) === 12 ? parseInt(year, 10) + 1 : parseInt(year, 10);
                const nextMonthLabel = nextMonth && nextYear ? `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][nextMonth - 1]} ${nextYear}` : '';
                const upcomingNetPay = monthlySalary > 0 ? Math.max(0, monthlySalary - advanceBalance) : 0;
                const upcomingFullyCovered = monthlySalary > 0 && advanceBalance >= monthlySalary;
                if (isSettled) {
                  return (
                    <>
                      {advanceBalance > 0 && (
                        <div className="salary-advance-balance-top">
                          <span className="salary-advance-label">Advance balance (to receive from salary):</span>
                          <span className="salary-advance-value balance-positive">{formatPKR(advanceBalance)}</span>
                        </div>
                      )}
                      <div className="salary-settled-box">
                        <p className="salary-settled-title">Already Settled</p>
                        <p className="salary-settled-text">
                          No payment required for {salaryOverview?.period_label || `${salaryFormData.periodMonth}/${salaryFormData.periodYear}`}.
                          Salary and any advances allocated to this month have already been fully covered.
                        </p>
                      </div>
                      {monthlySalary > 0 && nextMonthLabel && (
                        <div className="salary-upcoming-month-box">
                          <p className="salary-upcoming-title">Upcoming month ({nextMonthLabel})</p>
                          <p className="salary-upcoming-line"><span>Gross salary:</span> <span>{formatPKR(monthlySalary)}</span></p>
                          {advanceBalance > 0 && (
                            <p className="salary-upcoming-line">
                              <span>Less advance balance:</span> <span className="neg">({formatPKR(advanceBalance)})</span>
                            </p>
                          )}
                          <p className="salary-upcoming-net">
                            {upcomingFullyCovered
                              ? <><strong>Net to pay:</strong> <span className="salary-fully-covered">Fully covered by advance</span></>
                              : <><strong>Net to pay:</strong> <span>{formatPKR(upcomingNetPay)}</span></>
                            }
                          </p>
                        </div>
                      )}
                      <div className="modal-actions">
                        <button type="button" className="btn-primary" onClick={() => setShowSalaryModal(false)}>Close</button>
                      </div>
                    </>
                  );
                }
                return (
                  <>
                    <div className="salary-outstanding-box">
                      <p><strong>Outstanding for {salaryOverview?.period_label || `${salaryFormData.periodMonth}/${salaryFormData.periodYear}`}:</strong> {formatPKR(outstanding)}</p>
                      <p className="form-hint">Monthly salary: {formatPKR(selectedEmployee.monthly_salary)} · Paid/allocated: {formatPKR((selectedEmployee.paid_for_month || 0) + (selectedEmployee.advances_allocated_to_month || 0))}{(selectedEmployee.advances_allocated_to_month || 0) > 0 ? ' (includes advance; pay remainder at month end)' : ''}</p>
                    </div>
                    {selectedEmployee.balance != null && selectedEmployee.balance > 0 && (
                      <div className="salary-owe-box salary-they-owe-box">
                        <p className="salary-owe-line">
                          <strong>Employee owes us:</strong> <span className="balance-positive">{formatPKR(selectedEmployee.balance)} (To receive)</span>
                        </p>
                      </div>
                    )}
                    {monthlySalary > 0 && nextMonthLabel && (
                      <div className="salary-upcoming-month-box">
                        <p className="salary-upcoming-title">Upcoming month ({nextMonthLabel})</p>
                        <p className="salary-upcoming-line">
                          <span>Gross salary:</span> <span>{formatPKR(monthlySalary)}</span>
                        </p>
                        {advanceBalance > 0 && (
                          <p className="salary-upcoming-line">
                            <span>Less advance balance:</span> <span className="neg">({formatPKR(advanceBalance)})</span>
                          </p>
                        )}
                        <p className="salary-upcoming-net">
                          {upcomingFullyCovered
                            ? <><strong>Net to pay:</strong> <span className="salary-fully-covered">Fully covered by advance</span></>
                            : <><strong>Net to pay:</strong> <span>{formatPKR(Math.min(outstanding, upcomingNetPay))}</span></>
                          }
                        </p>
                      </div>
                    )}
                    <form onSubmit={handleSalarySubmit}>
                      <div className="form-group">
                        <label>Amount to pay (PKR) *</label>
                        <input
                          type="number"
                          min="0"
                          max={outstanding}
                          step="0.01"
                          value={salaryFormData.amount}
                          onChange={(e) => setSalaryFormData({ ...salaryFormData, amount: e.target.value })}
                          placeholder={String(outstanding)}
                          required
                        />
                        <small className="form-hint">Maximum for this month: {formatPKR(outstanding)}</small>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Salary for month *</label>
                          <select value={salaryFormData.periodMonth} onChange={(e) => setSalaryFormData({ ...salaryFormData, periodMonth: e.target.value })} required>
                            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                              <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'short' })}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Salary for year *</label>
                          <select value={salaryFormData.periodYear} onChange={(e) => setSalaryFormData({ ...salaryFormData, periodYear: e.target.value })} required>
                            {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Description</label>
                        <input type="text" value={salaryFormData.description} onChange={(e) => setSalaryFormData({ ...salaryFormData, description: e.target.value })} placeholder="e.g. Part 1 of 2" />
                      </div>
                      <div className="form-group">
                        <label>Payment Method</label>
                        <select value={salaryFormData.paymentMethod} onChange={(e) => setSalaryFormData({ ...salaryFormData, paymentMethod: e.target.value })}>
                          <option value="cash">Cash</option>
                          <option value="bank_transfer">Bank Transfer</option>
                          <option value="cheque">Cheque</option>
                          <option value="card">Card</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Pay from (Cash / Bank account) *</label>
                        <select
                          value={salaryFormData.accountId}
                          onChange={(e) => setSalaryFormData({ ...salaryFormData, accountId: e.target.value })}
                          required
                        >
                          <option value="">Select account or cash</option>
                          {fundAccounts.map(a => (
                            <option key={a.id} value={a.id}>
                              {a.name} ({a.type}) – {formatPKR(a.current_balance ?? a.currentBalance ?? 0)}
                            </option>
                          ))}
                        </select>
                        {salaryFormData.accountId && (() => {
                          const acc = fundAccounts.find(a => String(a.id) === String(salaryFormData.accountId));
                          const avail = parseFloat(acc?.current_balance ?? acc?.currentBalance ?? 0) || 0;
                          const amt = parseFloat(salaryFormData.amount) || 0;
                          const overOutstanding = amt > outstanding;
                          const insufficient = amt > avail;
                          return (
                            <>
                              <small className="form-hint">Available: {formatPKR(avail)} · Max for this month: {formatPKR(outstanding)}</small>
                              {overOutstanding && <p className="salary-insufficient-balance">Amount cannot exceed outstanding ({formatPKR(outstanding)}).</p>}
                              {insufficient && amt > 0 && !overOutstanding && (
                                <p className="salary-insufficient-balance">Insufficient balance in this account.</p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      <div className="form-group">
                        <label>Date *</label>
                        <input type="date" value={salaryFormData.transactionDate} onChange={(e) => setSalaryFormData({ ...salaryFormData, transactionDate: e.target.value })} required />
                      </div>
                      <div className="modal-actions">
                        <button type="button" onClick={() => setShowSalaryModal(false)}>Cancel</button>
                        <button
                          type="submit"
                          className="btn-primary"
                          disabled={
                            !salaryFormData.amount ||
                            parseFloat(salaryFormData.amount) <= 0 ||
                            parseFloat(salaryFormData.amount) > outstanding ||
                            !salaryFormData.accountId ||
                            (() => {
                              const acc = fundAccounts.find(a => String(a.id) === String(salaryFormData.accountId));
                              const avail = parseFloat(acc?.current_balance ?? acc?.currentBalance ?? 0) || 0;
                              const amt = parseFloat(salaryFormData.amount) || 0;
                              return amt > avail;
                            })()
                          }
                        >
                          Pay Salary
                        </button>
                      </div>
                    </form>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Ledger Modal - stay on Salary page */}
        {showLedgerModal && ledgerEmployee && (
          <div className="modal-overlay" onClick={() => setShowLedgerModal(false)}>
            <div className="modal-content modal-xlarge" onClick={(e) => e.stopPropagation()}>
              <h2>Ledger – {ledgerEmployee.name}</h2>
              {parseFloat(ledgerEmployee.balance) > 0 && (
                <div className="ledger-advance-balance-top">
                  <span className="ledger-advance-label">Advance balance (to receive from salary):</span>
                  <span className="ledger-advance-value balance-positive">{formatPKR(ledgerEmployee.balance)}</span>
                </div>
              )}
              <div className="ledger-summary">
                <p><strong>Current Balance:</strong> {formatBalance(ledgerEmployee.balance)}</p>
                <p className="ledger-summary-hint">
                  {parseFloat(ledgerEmployee.balance) > 0
                    ? 'This employee has received advance (to receive from salary).'
                    : parseFloat(ledgerEmployee.balance) < 0
                      ? 'You owe this employee (we owe).'
                      : 'No outstanding balance'}
                </p>
              </div>
              <div className="ledger-filters">
                <label>Type</label>
                <select value={ledgerFilterType} onChange={(e) => setLedgerFilterType(e.target.value)}>
                  <option value="all">All</option>
                  <option value="salary">Salary</option>
                  <option value="salary_advance">Advance</option>
                </select>
                <label>Month</label>
                <select value={ledgerFilterMonth} onChange={(e) => setLedgerFilterMonth(e.target.value)}>
                  <option value="">All</option>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'short' })}</option>
                  ))}
                </select>
                <label>Year</label>
                <select value={ledgerFilterYear} onChange={(e) => setLedgerFilterYear(e.target.value)}>
                  <option value="">All</option>
                  {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
                </select>
                <button type="button" className="btn-secondary btn-sm" onClick={handleLedgerFilterChange}>Apply</button>
              </div>
              <div className="ledger-table-container">
                {ledgerEntries.length === 0 ? (
                  <p className="no-entries">No ledger entries found.</p>
                ) : (
                  <table className="ledger-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>Allocation</th>
                        <th>Debit</th>
                        <th>Credit</th>
                        <th>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerEntries.map((entry) => (
                        <tr key={entry.id}>
                          <td>{new Date(entry.transaction_date).toLocaleDateString()}</td>
                          <td><span className={`type-badge ${(entry.reference_type || entry.transaction_type || '').replace(/\s+/g, '_')}`}>{entry.reference_type ? entry.reference_type.replace(/_/g, ' ') : (entry.transaction_type ? entry.transaction_type.replace(/_/g, ' ') : '—')}</span></td>
                          <td>
                            <div>{entry.description || '—'}</div>
                            {entry.meta?.paymentMethod && (
                              <small className="ledger-meta">
                                Method: {entry.meta.paymentMethod}
                                {entry.meta.referenceNumber && ` • Ref: ${entry.meta.referenceNumber}`}
                              </small>
                            )}
                          </td>
                          <td>{entry.allocation_label || (entry.meta?.periodMonth && entry.meta?.periodYear ? `${entry.meta.periodMonth}/${entry.meta.periodYear}` : '—')}</td>
                          <td className="debit">{parseFloat(entry.debit) > 0 ? formatPKR(entry.debit) : '—'}</td>
                          <td className="credit">{parseFloat(entry.credit) > 0 ? formatPKR(entry.credit) : '—'}</td>
                          <td>{formatBalance(entry.balance_after ?? entry.running_balance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowLedgerModal(false)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Employee Modal */}
        {showEditModal && editingParty && (
          <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Edit Employee – {editingParty.name}</h2>
              <form onSubmit={handleEditSubmit}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <CitySelect
                    value={editFormData.city}
                    onChange={(city) => setEditFormData({ ...editFormData, city })}
                    required
                    placeholder="Select city"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Monthly salary (PKR)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editFormData.salary}
                    onChange={(e) => setEditFormData({ ...editFormData, salary: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>Advance balance (PKR) – update if employee has received advance</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editFormData.employeeAdvance}
                    onChange={(e) => setEditFormData({ ...editFormData, employeeAdvance: e.target.value })}
                    placeholder="e.g. 5000"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={editFormData.salaryPaidForCurrentMonth}
                      onChange={(e) => setEditFormData({ ...editFormData, salaryPaidForCurrentMonth: e.target.checked })}
                    />
                    <span>{currentMonthName} {currentYear} salary already given</span>
                  </label>
                  <small className="form-hint">Check if salary for {currentMonthName} {currentYear} has already been paid for this employee.</small>
                </div>
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowEditModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Record Advance Modal */}
        {showAdvanceModal && selectedEmployeeForAdvance && (
          <div className="modal-overlay" onClick={() => setShowAdvanceModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Record Advance – {selectedEmployeeForAdvance.name}</h2>
              <p className="form-hint" style={{ marginBottom: 12 }}>Advance from <strong>{currentMonthName} {currentYear}</strong> salary (current month). That month will show as <strong>Partially paid</strong> until you pay the remainder at month end.</p>
              <form onSubmit={handleAdvanceSubmit}>
                <div className="form-group">
                  <label>Amount *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={advanceFormData.amount}
                    onChange={(e) => setAdvanceFormData({ ...advanceFormData, amount: e.target.value })}
                    placeholder="Enter amount"
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Allocate to month *</label>
                    <select
                      value={advanceFormData.periodMonth}
                      onChange={(e) => setAdvanceFormData({ ...advanceFormData, periodMonth: e.target.value })}
                      required
                    >
                      <option value="">Select month</option>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('default', { month: 'long' })}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Allocate to year *</label>
                    <select
                      value={advanceFormData.periodYear}
                      onChange={(e) => setAdvanceFormData({ ...advanceFormData, periodYear: e.target.value })}
                      required
                    >
                      <option value="">Select year</option>
                      {years.map(yr => (
                        <option key={yr} value={yr}>{yr}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Account (Cash / Bank)</label>
                  <select value={advanceFormData.accountId} onChange={(e) => setAdvanceFormData({ ...advanceFormData, accountId: e.target.value })}>
                    <option value="">Default (Cash)</option>
                    {fundAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={advanceFormData.description}
                    onChange={(e) => setAdvanceFormData({ ...advanceFormData, description: e.target.value })}
                    placeholder="e.g. Advance against salary"
                  />
                </div>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select value={advanceFormData.paymentMethod} onChange={(e) => setAdvanceFormData({ ...advanceFormData, paymentMethod: e.target.value })}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={advanceFormData.transactionDate}
                    onChange={(e) => setAdvanceFormData({ ...advanceFormData, transactionDate: e.target.value })}
                    required
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowAdvanceModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary">Record Advance</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showDeleteConfirm}
          onClose={() => { setShowDeleteConfirm(false); setEmployeeToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete Employee"
          message="Are you sure you want to delete this employee? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />

        {/* Add Employee Modal */}
        {showAddEmployeeModal && (
          <div className="modal-overlay" onClick={() => setShowAddEmployeeModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Add Employee</h2>
              {!hasBranches ? (
                <div className="banner no-branches-banner" style={{ marginTop: 0 }}>
                  <strong>You need a branch first.</strong> Every employee is linked to a branch. Create a branch from <a href="/branches">Branches</a>, then you can add employees here.
                </div>
              ) : null}
              {hasBranches && currentBranch && (
                <p className="party-linked-branch" style={{ marginBottom: '1rem', fontSize: 14, color: '#374151' }}>
                  This employee will be linked to: <strong style={{ color: '#7c3aed' }}>{currentBranch.name}</strong>
                </p>
              )}
              <form onSubmit={handleAddEmployeeSubmit}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={addEmployeeFormData.name}
                    onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, name: e.target.value })}
                    placeholder="e.g. Ahmed Khan"
                    required
                    minLength={2}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={addEmployeeFormData.phone}
                    onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, phone: e.target.value })}
                    placeholder="e.g. +92 300 1234567"
                  />
                </div>
                <div className="form-group">
                  <label>City *</label>
                  <CitySelect
                    value={addEmployeeFormData.city}
                    onChange={(city) => setAddEmployeeFormData({ ...addEmployeeFormData, city })}
                    required
                    placeholder="Select city"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Monthly salary (PKR)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={addEmployeeFormData.salary}
                    onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, salary: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>Advance amount (PKR) – if employee has already received any advance</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={addEmployeeFormData.employeeAdvance}
                    onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, employeeAdvance: e.target.value })}
                    placeholder="e.g. 5000"
                  />
                  <small className="form-hint">This balance will be shown on top and deducted from upcoming salary.</small>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={addEmployeeFormData.salaryPaidForCurrentMonth}
                      onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, salaryPaidForCurrentMonth: e.target.checked })}
                    />
                    <span>{currentMonthName} {currentYear} salary already given</span>
                  </label>
                  <small className="form-hint">Check if salary for {currentMonthName} {currentYear} has already been paid for this employee.</small>
                </div>
                {!addEmployeeFormData.salaryPaidForCurrentMonth && parseFloat(addEmployeeFormData.salary) > 0 && (
                  <div className="form-group">
                    <label>Deduct {currentMonthName} {currentYear} salary from (account)</label>
                    <select
                      value={addEmployeeFormData.deductCurrentMonthSalaryFromAccountId}
                      onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, deductCurrentMonthSalaryFromAccountId: e.target.value })}
                    >
                      <option value="">Leave unpaid / pay later from Salary page</option>
                      {fundAccounts.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.name} ({a.type}) – {formatPKR(a.current_balance ?? a.currentBalance ?? 0)}
                        </option>
                      ))}
                    </select>
                    <small className="form-hint">Optional. If you select an account, {currentMonthName} salary will be recorded when the employee is created.</small>
                  </div>
                )}
                <div className="form-group">
                  <label>Notes</label>
                  <input
                    type="text"
                    value={addEmployeeFormData.notes}
                    onChange={(e) => setAddEmployeeFormData({ ...addEmployeeFormData, notes: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => setShowAddEmployeeModal(false)}>Cancel</button>
                  <button type="submit" className="btn-primary" disabled={!hasBranches}>Add Employee</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      {salarySlipMenu && salaryOverview && createPortal(
        <div
          className={`salary-slip-menu salary-slip-menu-portal${salarySlipMenu.openAbove ? ' open-above' : ''}`}
          role="menu"
          style={{ top: salarySlipMenu.top, left: salarySlipMenu.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="salary-slip-menu-item"
            role="menuitem"
            onClick={() => handleDownloadMonthlySalarySlip(salarySlipMenu.employee)}
            disabled={salarySlipDownloadingId === salarySlipMenu.employee.id}
          >
            Monthly slip — {salaryOverview.period_label}
          </button>
          <button
            type="button"
            className="salary-slip-menu-item"
            role="menuitem"
            onClick={() => handleDownloadAnnualSalarySlip(salarySlipMenu.employee)}
            disabled={salarySlipDownloadingId === salarySlipMenu.employee.id}
          >
            Annual slip — {year}
          </button>
        </div>,
        document.body
      )}
    </Layout>
  );
};

export default Salary;
